const ONLINE_THRESHOLD_MS = 120_000; // 2 minutes

/** Parse a Firestore Timestamp or epoch number into a Date. */
const toDate = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  const ts = timestamp as { toDate?: () => Date };
  return ts.toDate ? ts.toDate() : new Date(timestamp as number);
};

/** Whether a user is considered online based on their lastSeen value. */
export const isOnline = (lastSeen: number): boolean =>
  Date.now() - lastSeen < ONLINE_THRESHOLD_MS;

/** Format a timestamp for the conversation list (e.g. "2:30 PM", "Yesterday", "Apr 12"). */
export const formatConversationTime = (timestamp: unknown): string => {
  const date = toDate(timestamp);
  if (!date) return '';

  const diff = Date.now() - date.getTime();
  if (diff < 86_400_000) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diff < 172_800_000) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

/** Format a timestamp for individual messages (e.g. "2:30 PM"). */
export const formatMessageTime = (timestamp: unknown): string => {
  const date = toDate(timestamp);
  if (!date) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/** Format a date separator (e.g. "Today", "Yesterday", "Monday, April 12"). */
export const formatDateSeparator = (timestamp: unknown): string => {
  const date = toDate(timestamp);
  if (!date) return '';

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

/** Format a lastSeen timestamp into a relative string. */
export const formatLastSeen = (lastSeen: number): string => {
  const diff = Date.now() - lastSeen;
  if (diff < ONLINE_THRESHOLD_MS) return 'Online';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(lastSeen).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
